// Per-user product-tour progress (VRG-228 — E4). API client for /tour-progress.
//
// Types are declared locally rather than imported from `@repo/types`, matching the
// house convention that apps/platform does not depend on that package (see
// `services/supportApi.ts`, which says the same). The canonical definitions live
// in `apps/api/src/features/product-tours/product-tours.types.ts`; keep the two
// in step.

import { createApiCall } from './index'

/**
 * `in_progress` — started, not finished. NOT terminal: a tour left half-done is
 * still allowed to auto-fire again.
 * `completed`  — ran to the end.
 * `dismissed`  — closed early, or marked seen without running.
 */
export type TourProgressStatus = 'in_progress' | 'completed' | 'dismissed'

export interface TourProgress {
  tourKey: string
  status: TourProgressStatus
  currentStep: number
  completedAt: string | null
  updatedAt: string
}

/**
 * "Do not auto-start this again."
 *
 * Defined once, here, because it is the predicate the whole feature turns on and
 * the server mirrors it (`isTerminalTourStatus`). `in_progress` is deliberately
 * NOT terminal — someone who abandoned a tour halfway has not seen it.
 */
export const isTerminalTourStatus = (status: TourProgressStatus): boolean =>
  status === 'completed' || status === 'dismissed'

/** Carries the HTTP status, so a caller can tell "not allowed" from "failed". */
export class TourProgressApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
    this.name = 'TourProgressApiError'
  }
}

// `createApiCall`'s errorHandler RESOLVES with the error response rather than
// throwing on non-401 failures (see services/index.ts), so every call here has to
// check the status itself. Silently resolving with an error body is how a hook
// ends up treating a 500 as "no tours seen" — which, for this feature, means
// replaying every tour the user already finished.
const ok = (response: any, fallback: string) => {
  if (response.status !== 200 && response.status !== 201) {
    throw new TourProgressApiError(
      response?.data?.message || fallback,
      response?.status ?? 0,
    )
  }
  return response.data
}

export const tourProgressApi = {
  /** Every tour the CURRENT user has state for. The user is taken from the session. */
  async list(): Promise<TourProgress[]> {
    const response = await createApiCall({ method: 'GET', url: '/tour-progress' })
    const data = ok(response, 'Failed to load tour progress')
    // Defensive: a proxy or an error shape that slipped past `ok` must not become
    // `progress.forEach is not a function` inside the provider that wraps the app.
    return Array.isArray(data) ? data : []
  },

  /** Idempotent upsert — safe to call repeatedly for the same tour. */
  async upsert(
    tourKey: string,
    payload: { status: TourProgressStatus; currentStep?: number },
  ): Promise<TourProgress> {
    const response = await createApiCall({
      method: 'PUT',
      url: `/tour-progress/${encodeURIComponent(tourKey)}`,
      data: payload,
    })
    return ok(response, 'Failed to save tour progress')
  },
}
